import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Clock, Users, CheckCircle, X, Eye, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { venueGamesService, VenueGame } from '@/services/venueGamesService';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface VenueGameApprovalProps {
  venueId: string;
}

export const VenueGameApproval: React.FC<VenueGameApprovalProps> = ({ venueId }) => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;
  const { toast } = useToast();
  
  const [pendingGames, setPendingGames] = useState<VenueGame[]>([]);
  const [approvedGames, setApprovedGames] = useState<VenueGame[]>([]);
  const [rejectedGames, setRejectedGames] = useState<VenueGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState<VenueGame | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);

  useEffect(() => {
    loadAllGames();
  }, [venueId]);

  const loadAllGames = async () => {
    setLoading(true);
    try {
      console.log('🎯 APPROVAL - Loading all games for venue:', venueId);
      
      // Load pending games
      const { games: pending } = await venueGamesService.getPendingSubmissions(venueId);
      setPendingGames(pending);
      console.log('🎯 APPROVAL - Loaded pending games:', pending.length);
      
      // Load approved games
      const approved = await venueGamesService.getApprovedGames(venueId);
      setApprovedGames(approved);
      console.log('🎯 APPROVAL - Loaded approved games:', approved.length);
      
      // Load rejected games (last 30 days)
      const rejected = await venueGamesService.getRejectedGames(venueId);
      setRejectedGames(rejected);
      console.log('🎯 APPROVAL - Loaded rejected games:', rejected.length);
      
    } catch (error) {
      console.error('❌ APPROVAL - Error loading games:', error);
      toast({
        title: "Error loading games",
        description: "Failed to load game requests",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGameAction = (game: VenueGame, action: 'approve' | 'reject') => {
    setSelectedGame(game);
    setConfirmAction(action);
    setShowConfirmDialog(true);
  };

  const confirmGameAction = async () => {
    if (!selectedGame || !confirmAction || !user) return;

    try {
      const mappedStatus = confirmAction === 'approve' ? 'approved' : 'rejected';
      await venueGamesService.updateGameStatus(selectedGame.id, mappedStatus, user.id);
      
      toast({
        title: `Game ${confirmAction}d successfully`,
        description: `The game has been ${confirmAction}d and teams will be notified`,
      });
      
      await loadAllGames();
      setShowConfirmDialog(false);
      setSelectedGame(null);
      setConfirmAction(null);
    } catch (error) {
      console.error(`Error ${confirmAction}ing game:`, error);
      toast({
        title: "Error",
        description: `Failed to ${confirmAction} game`,
        variant: "destructive"
      });
    }
  };

  const GameCard: React.FC<{ game: VenueGame; showActions?: boolean }> = ({ game, showActions = false }) => {
    const getStatusBadge = (status: string) => {
      switch (status) {
        case 'pending':
          return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500">Pending</Badge>;
        case 'approved':
          return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500">Approved</Badge>;
        case 'rejected':
          return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500">Rejected</Badge>;
        default:
          return <Badge variant="outline">{status}</Badge>;
      }
    };

    const getTeamName = (team: any): string => {
      if (!team) return 'TBD';
      if (Array.isArray(team)) return team[0]?.team_name || 'TBD';
      return team.team_name || 'TBD';
    };

    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-white">
              {game.game_title || `${getTeamName(game.home_team)} vs ${getTeamName(game.away_team)}`}
            </CardTitle>
            {getStatusBadge(game.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar className="h-4 w-4" />
              {format(new Date(game.game_date), 'MMM dd, yyyy')}
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Clock className="h-4 w-4" />
              {formatGameTime(game.game_time)}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-300">
              <Users className="h-4 w-4" />
              <span className="font-medium">Teams:</span>
            </div>
            <div className="pl-6 space-y-1 text-sm">
              <div className="text-gray-400">Home: {getTeamName(game.home_team)}</div>
              <div className="text-gray-400">Away: {getTeamName(game.away_team)}</div>
            </div>
          </div>

          {game.game_description && (
            <div className="space-y-2">
              <span className="text-gray-300 font-medium">Description:</span>
              <p className="text-gray-400 text-sm">{game.game_description}</p>
            </div>
          )}

          <div className="text-xs text-gray-500">
            Game Code: {game.game_code} | Field: {game.field_location}
          </div>

          {showActions && game.status === 'pending' && (
            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                onClick={() => handleGameAction(game, 'approve')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleGameAction(game, 'reject')}
              >
                <X className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const formatGameTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      const formattedTime = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `${formattedTime} CST`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading game requests...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Game Approval Center</h2>
          <p className="text-gray-400">Manage game requests for your venue</p>
        </div>
        <Button onClick={loadAllGames} variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gray-800 border border-gray-700">
          <TabsTrigger value="pending" className="text-white data-[state=active]:bg-yellow-600">
            Pending ({pendingGames.length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="text-white data-[state=active]:bg-green-600">
            Approved ({approvedGames.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="text-white data-[state=active]:bg-red-600">
            Rejected ({rejectedGames.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {pendingGames.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <AlertTriangle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No pending game requests</p>
                <p className="text-gray-500 text-sm mt-2">New game requests will appear here for your approval</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingGames.map((game) => (
                <GameCard key={game.id} game={game} showActions={true} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {approvedGames.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <CheckCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No approved games</p>
                <p className="text-gray-500 text-sm mt-2">Approved games will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {approvedGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4">
          {rejectedGames.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6 text-center">
                <X className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No rejected games</p>
                <p className="text-gray-500 text-sm mt-2">Rejected games from the last 30 days will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {rejectedGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {confirmAction === 'approve' ? 'Approve Game' : 'Reject Game'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {confirmAction === 'approve' 
                ? 'Are you sure you want to approve this game? Teams will be notified and can proceed with roster management.'
                : 'Are you sure you want to reject this game? This action will notify the requesting party.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {selectedGame && (
            <div className="py-4">
              <div className="space-y-2 text-sm">
                <div className="text-gray-300">
                  <strong>Game:</strong> {selectedGame.game_title}
                </div>
                <div className="text-gray-300">
                  <strong>Date:</strong> {format(new Date(selectedGame.game_date), 'MMM dd, yyyy')} at {formatGameTime(selectedGame.game_time)}
                </div>
                <div className="text-gray-300">
                  <strong>Teams:</strong> {selectedGame.home_team ? 
                    (Array.isArray(selectedGame.home_team) ? selectedGame.home_team[0]?.team_name : selectedGame.home_team.team_name) || 'TBD' 
                    : 'TBD'} vs {selectedGame.away_team ? 
                    (Array.isArray(selectedGame.away_team) ? selectedGame.away_team[0]?.team_name : selectedGame.away_team.team_name) || 'TBD' 
                    : 'TBD'}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmGameAction}
              className={confirmAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {confirmAction === 'approve' ? 'Approve Game' : 'Reject Game'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};